import {
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Text,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { getDeviceIssueEmailSuccessToastMessage } from '@/components/People/EmployeeProfile/helpers/DevicePropertyHelpers';
import { useSendIssueEmailMutation } from '@/redux/slices/deviceApiSlice';

import { DeviceIssueType } from '@/types/devices';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { AssetDeviceProperty } from '@/types/assets';

type EmailModalProps = {
  hide: () => void;
  issueType: DeviceIssueType;
  employeeId: string;
  employeeFullname: string;
  employeeEmail: string | null;
  title: string;
  children: JSX.Element | JSX.Element[];
  device: AssetDeviceProperty;
};

const EmailModal = ({
  hide,
  issueType,
  employeeId,
  employeeFullname,
  employeeEmail,
  title,
  children,
  device,
}: EmailModalProps) => {
  const { t } = useTranslation('assets');
  const { showSuccessToast, showErrorToast } = useGlobalUI();

  const [sendIssueEmail, { isLoading }] = useSendIssueEmailMutation();
  const organizationId = useGetOrganizationId();

  const issueTypeToast = getDeviceIssueEmailSuccessToastMessage(issueType);

  const handleEmailSendError = (error?: Error) => {
    console.error(`Unable to send ${issueTypeToast} instructions: `, error);
    showErrorToast({
      id: 'email-send-error-toast',
      stack: !!error,
      title: t('deviceSecurityPanel.emailModal.unableToSendEmails'),
      message: t('deviceSecurityPanel.emailModal.emailSendError'),
    });
  };

  const handleSubmit = () => {
    if (organizationId) {
      const employeeDeviceOsGrouping = [
        {
          employee_id: employeeId,
          os_type: device.osType || 'darwin',
          device_id: device.id || '',
        },
      ];

      sendIssueEmail({
        employeeDeviceOsGrouping,
        issueType,
        organizationId,
      })
        .unwrap()
        .then(() => {
          showSuccessToast({
            id: 'email-send-success-toast',
            stack: true,
            message: t('deviceSecurityPanel.emailModal.emailSentSuccess', {
              issueType: issueTypeToast,
              employeeName: employeeFullname,
            }),
          });
          hide();
        })
        .catch(handleEmailSendError);
    } else {
      handleEmailSendError();
    }
  };

  return (
    <ModalV2 visible hide={hide} ariaLabelledby="email-employees">
      <ModalHeader>
        <Text variant="heading-2">{title}</Text>
      </ModalHeader>
      <ModalBody padding="3.2rem 1.6rem">{children}</ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction
            id="send-email-cancel-button"
            variant="text"
            onClick={hide}
            disabled={isLoading}>
            {t('deviceSecurityPanel.emailModal.cancel')}
          </ModalAction>
          <ModalAction
            id="send-email-button"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={employeeEmail === null}>
            {t('deviceSecurityPanel.emailModal.sendEmail')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default EmailModal;
